<template>
	<ContainerCenter>
		<v-card>
			<v-toolbar
				color="primary"
				dark
				flat
			>
				<v-toolbar-title>{{ confirmacaoDeEmail ? 'Confirmar e-mail' : 'Definir senha' }}</v-toolbar-title>
			</v-toolbar>
			<v-card-text v-if="verificando">
				<div class="text-center">
					<v-progress-circular indeterminate />
				</div>
			</v-card-text>
			<v-card-text v-else-if="linkInvalido">
				<div class="body-1 text-center">
					Este link expirou ou já foi usado. Peça um novo link.
				</div>
			</v-card-text>
			<v-card-text v-else-if="confirmacaoDeEmail">
				<div class="body-1 text-center">
					E-mail confirmado!
					<br> A partir de agora, entre com <strong>{{ email }}</strong> e a sua senha.
				</div>
			</v-card-text>
			<v-card-text v-else>
				<v-form
					ref="form"
					@submit.prevent="definirSenha"
					id="form-definir-senha"
				>
					<v-text-field
						name="email"
						label="E-mail"
						:value="email"
						autocomplete="username"
						disabled
					/>
					<v-text-field
						autofocus
						name="senha"
						label="Nova senha"
						:type="verSenha ? 'text' : 'password'"
						v-model="senha"
						:append-icon="verSenha ? 'mdi-eye-off' : 'mdi-eye'"
						@click:append="() => verSenha = !verSenha"
						:rules="[ obrigatorio, senhaMinima ]"
						autocomplete="new-password"
					/>
					<v-text-field
						name="confirmar-senha"
						label="Confirmar senha"
						v-model="confirmarSenha"
						type="password"
						validate-on-blur
						autocomplete="new-password"
						:rules="[ obrigatorio, coincidirSenhas ]"
					/>
				</v-form>
			</v-card-text>
			<v-card-actions v-if="!verificando && !linkInvalido && !confirmacaoDeEmail">
				<v-btn
					block
					color="primary"
					type="submit"
					form="form-definir-senha"
					:disabled="carregando"
					:loading="carregando"
				> Salvar e entrar </v-btn>
			</v-card-actions>
			<div class="text-center pb-3">
				<router-link
					v-if="linkInvalido && !confirmacaoDeEmail"
					:to="{ name: 'esqueci-senha' }"
				>Pedir novo link</router-link>
				<router-link
					v-else-if="confirmacaoDeEmail && !linkInvalido"
					:to="{ name: 'login', query: { email } }"
				>Entrar</router-link>
				<router-link
					v-else
					:to="{ name: 'login' }"
				>Voltar para o login</router-link>
			</div>
		</v-card>
	</ContainerCenter>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import ContainerCenter from '@/components/ui/ContainerCenter.vue'
import { obrigatorio, senhaMinima } from '@/shareds/regras'
import { auth } from '@/firebase'
import { loginFirebase } from '@/services/AuthService'
import { alert, authModule } from '@/store'
import { useRoute, useRouter } from '@/composables/router'

// o Firebase manda para esta página (URL de ação do console) os links de todos os e-mails de conta
const route = useRoute()
const router = useRouter()

const form = ref<HTMLFormElement & {
	validate: () => boolean,
}>()

const codigo = String(route.query.oobCode ?? '')
const confirmacaoDeEmail = route.query.mode === 'verifyEmail'
const verificando = ref(true)
const linkInvalido = ref(false)
const carregando = ref(false)
const verSenha = ref(false)
const email = ref('')
const senha = ref('')
const confirmarSenha = ref('')

function coincidirSenhas(confirmacao: unknown) {
	return confirmacao === senha.value || 'Senhas não coincidem'
}

onMounted(async () => {
	try {
		if (!codigo) throw new Error('Link sem código')
		if (confirmacaoDeEmail) {
			const { data } = await auth.checkActionCode(codigo)
			email.value = data.email ?? ''
			await auth.applyActionCode(codigo)
		} else {
			email.value = await auth.verifyPasswordResetCode(codigo)
		}
	} catch {
		linkInvalido.value = true
	} finally {
		verificando.value = false
	}
})

async function definirSenha() {
	if (!form.value!.validate()) return
	try {
		carregando.value = true
		await auth.confirmPasswordReset(codigo, senha.value)
		const { user } = await auth.signInWithEmailAndPassword(email.value, senha.value)
		const { data } = await loginFirebase({ idToken: await user!.getIdToken(), senha: senha.value })
		await authModule.logarToken(data)
		router.push({ name: 'home' })
			.catch(() => {})
	} catch (error) {
		const codigoDoErro = (error as { code?: string }).code
		if (codigoDoErro === 'auth/expired-action-code' || codigoDoErro === 'auth/invalid-action-code') {
			linkInvalido.value = true
			return
		}
		if (codigoDoErro === 'auth/weak-password') {
			alert.setError('A senha deve ter pelo menos 6 caracteres')
			return
		}
		alert.setError(error)
	} finally {
		carregando.value = false
	}
}
</script>
